// Japanese scoring: dead-stone seeding from KataGo ownership, manual dead/dame
// toggles, territory flood-fill with dead stones removed, and the final
// ScoreBreakdown arithmetic.
//
// Seki is best-effort by design: shared liberties become dame automatically
// (mixed-border regions), one-sided eye cases are covered by KataGo seeding
// plus manual dame toggles (forcedDame).

import type { Color, Pt, ScoreBreakdown, Vertex } from '../types.ts';
import { BLACK, EMPTY, WHITE, chainAt, type Position } from './board.ts';
import { neighborsOf } from './board.ts';
import { ptToVertex, vertexToPt } from './coords.ts';

const DEAD_THRESHOLD = 0.6;                   // |mean chain ownership| beyond this, against the chain -> dead
const DAME_THRESHOLD = 0.3;                   // empty points with |ownership| below this seed forcedDame

export interface DeadMarkSeed {
  deadStones: Vertex[];
  forcedDame: Vertex[];
}

/**
 * Seed dead-stone and dame marks from a KataGo ownership map
 * (length size*size, indexed by Pt with y=0 = TOP row, black-positive in [-1,1]).
 * A chain is dead when its mean ownership from its own color's perspective is
 * below -0.6; empty points with |ownership| < 0.3 seed forcedDame.
 */
export function markDeadFromOwnership(pos: Position, ownership: ArrayLike<number>): DeadMarkSeed {
  const n = pos.size * pos.size;
  if (ownership.length !== n) {
    throw new Error(`markDeadFromOwnership: ownership length ${ownership.length}, expected ${n}`);
  }
  const deadStones: Vertex[] = [];
  const forcedDame: Vertex[] = [];
  const visited = new Uint8Array(n);

  for (let pt = 0; pt < n; pt++) {
    const state = pos.stones[pt]!;
    if (state === EMPTY) {
      if (Math.abs(ownership[pt]!) < DAME_THRESHOLD) forcedDame.push(ptToVertex(pt, pos.size));
      continue;
    }
    if (visited[pt]) continue;
    const chain = chainAt(pos.stones, pos.size, pt);
    let sum = 0;
    for (const p of chain.points) {
      visited[p] = 1;
      sum += ownership[p]!;
    }
    const mean = sum / chain.points.length;
    const fromOwnPerspective = state === BLACK ? mean : -mean;
    if (fromOwnPerspective < -DEAD_THRESHOLD) {
      for (const p of chain.points) deadStones.push(ptToVertex(p, pos.size));
    }
  }
  return { deadStones, forcedDame };
}

/**
 * Toggle the dead mark of the WHOLE chain containing `vertex`. If every stone
 * of the chain is already marked dead, the chain is unmarked; otherwise the
 * whole chain is marked. Empty vertices return the marks unchanged.
 */
export function toggleDeadGroup(pos: Position, deadStones: Vertex[], vertex: Vertex): Vertex[] {
  const pt = vertexToPt(vertex, pos.size);
  if (pos.stones[pt] === EMPTY) return [...deadStones];
  const chain = chainAt(pos.stones, pos.size, pt);
  const chainVertices = chain.points.map((p) => ptToVertex(p, pos.size));
  const dead = new Set(deadStones);
  if (chainVertices.every((v) => dead.has(v))) {
    for (const v of chainVertices) dead.delete(v);
  } else {
    for (const v of chainVertices) dead.add(v);
  }
  return [...dead].sort((a, b) => vertexToPt(a, pos.size) - vertexToPt(b, pos.size));
}

export interface TerritoryResult {
  territory: { B: Vertex[]; W: Vertex[] };
  dame: Vertex[];
}

/**
 * Flood-fill territory with dead stones removed. An empty region bordered by
 * stones of exactly one color is that color's territory; mixed or colorless
 * regions are dame. forcedDame points are excluded from regions and always
 * counted as dame.
 */
export function computeTerritory(
  pos: Position,
  deadStones: Vertex[],
  forcedDame: Vertex[],
): TerritoryResult {
  const size = pos.size;
  const n = size * size;
  const working = pos.stones.slice();
  for (const v of deadStones) working[vertexToPt(v, size)] = EMPTY;

  const damePts = new Set<Pt>();
  for (const v of forcedDame) {
    const pt = vertexToPt(v, size);
    if (working[pt] === EMPTY) damePts.add(pt);
  }

  const territory: { B: Pt[]; W: Pt[] } = { B: [], W: [] };
  const visited = new Uint8Array(n);

  for (let start = 0; start < n; start++) {
    if (visited[start] || working[start] !== EMPTY || damePts.has(start)) continue;
    const region: Pt[] = [];
    const stack: Pt[] = [start];
    visited[start] = 1;
    let bordersBlack = false;
    let bordersWhite = false;
    while (stack.length > 0) {
      const pt = stack.pop()!;
      region.push(pt);
      for (const nb of neighborsOf(size, pt)) {
        const state = working[nb];
        if (state === BLACK) bordersBlack = true;
        else if (state === WHITE) bordersWhite = true;
        else if (!visited[nb] && !damePts.has(nb)) {
          visited[nb] = 1;
          stack.push(nb);
        }
      }
    }
    if (bordersBlack && !bordersWhite) territory.B.push(...region);
    else if (bordersWhite && !bordersBlack) territory.W.push(...region);
    else for (const pt of region) damePts.add(pt);
  }

  const toVertices = (pts: Pt[]): Vertex[] =>
    pts.sort((a, b) => a - b).map((pt) => ptToVertex(pt, size));
  return {
    territory: { B: toVertices(territory.B), W: toVertices(territory.W) },
    dame: toVertices([...damePts]),
  };
}

export interface ScoreInputs {
  komi: number;
  captures: { B: number; W: number };         // stones captured during play, BY each color
  deadStones: Vertex[];
  forcedDame: Vertex[];
}

/**
 * Japanese score: territory + prisoners (+ komi for White). Prisoners =
 * captures during play + the opponent's dead stones removed at the end.
 */
export function scoreJapanese(pos: Position, inputs: ScoreInputs): ScoreBreakdown {
  const { komi, captures, forcedDame } = inputs;

  // Keep only dead marks that actually sit on stones, deduplicated by board
  // point (also collapses case aliases like 'b8'/'B8'); note their colors.
  const deadStones: Vertex[] = [];
  const seenDead = new Set<Pt>();
  let deadBlack = 0;
  let deadWhite = 0;
  for (const v of inputs.deadStones) {
    const pt = vertexToPt(v, pos.size);
    if (seenDead.has(pt)) continue;
    const state = pos.stones[pt];
    if (state === BLACK) deadBlack += 1;
    else if (state === WHITE) deadWhite += 1;
    else continue;
    seenDead.add(pt);
    deadStones.push(v);
  }

  const { territory, dame } = computeTerritory(pos, deadStones, forcedDame);

  const black = {
    territory: territory.B.length,
    prisoners: captures.B + deadWhite,
    total: 0,
  };
  black.total = black.territory + black.prisoners;
  const white = {
    territory: territory.W.length,
    prisoners: captures.W + deadBlack,
    komi,
    total: 0,
  };
  white.total = white.territory + white.prisoners + komi;

  const diff = Math.round((black.total - white.total) * 2) / 2;
  const winner: Color | null = diff === 0 ? null : diff > 0 ? 'B' : 'W';
  const result = winner === null ? 'Draw' : `${winner}+${Math.abs(diff)}`;

  return {
    komi,
    black,
    white,
    damePoints: dame,
    territoryPoints: territory,
    deadStones,
    result,
    winner,
  };
}
